import {Button, PageSectionContainer, Text, TextInternal} from '~/components';
import SanityImage from '~/components/media/SanityImage.client';
import clsx from 'clsx';
// @ts-ignore
import {Swiper, SwiperSlide} from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';
import {Link} from '@shopify/hydrogen';
import {ButtonLinkEntity, ThreeCardEntity, ThreeCardItem} from '~/types';
import {ButtonVariant} from '~/lib/types';
import {PostCategories} from '../elements/PostCategories';
import {DATA_TEST_IDS} from '~/lib/const';

function Card({
  image,
  title,
  description,
  link,
  openInNewTab,
  categories,
}: ThreeCardItem) {
  const linkProps: any = {};

  if (link != null) {
    linkProps.to = link;
    if (openInNewTab) {
      linkProps.target = '_blank';
      linkProps.rel = 'noreferrer noopener';
    }
  }

  const LinkComp = link != null ? Link : 'div';

  return (
    <div data-testid={DATA_TEST_IDS.CARD}>
      <LinkComp {...linkProps} className={'relative'}>
        {image?.image?.asset?._ref == null ? (
          <div
            className={
              'bg-gray-100 rounded-[10px] md:rounded-none w-full aspect-square'
            }
          />
        ) : (
          <div className="aspect-square">
            <SanityImage
              className={'rounded-[10px] md:rounded-none object-cover'}
              objectFit="cover"
              crop={image?.image.crop}
              hotspot={image?.image.hotspot}
              layout="fill"
              src={image?.image?.asset?._ref}
              breakpoints={[
                [230, 230],
                [434, 434],
              ]}
              alt={image?.image?.altText ?? title}
            />
          </div>
        )}
      </LinkComp>
      {title && (
        <div className={'mt-2.5 md:mt-5 w-[380px] max-w-full'}>
          <LinkComp {...linkProps}>
            <Text
              testid={DATA_TEST_IDS.CARD_TITLE}
              as={'h5'}
              className={
                'text-sm leading-[22px] md:text-2lg font-bold break-words'
              }
            >
              {title}
            </Text>
          </LinkComp>
        </div>
      )}
      {description && (
        <div className={'mt-2.5 w-[380px] max-w-full flex'}>
          <LinkComp {...linkProps} className={'flex'}>
            <Text
              testid={DATA_TEST_IDS.CARD_DESCRIPTION}
              className={
                'text-[13px] leading-[18px] md:text-base text-gray-400 md:tracking-wider font-bold font-header break-words'
              }
            >
              {description}
            </Text>
          </LinkComp>
        </div>
      )}

      {categories && (
        <PostCategories
          categories={categories}
          className="flex mt-2.5"
          limit={2}
        />
      )}
    </div>
  );
}

const ActionButton = ({
  button,
  variant,
}: {
  button: ButtonLinkEntity;
  variant?: ButtonVariant;
}) => {
  const {buttonText, buttonHref, buttonType, buttonWidth, openInNewTab} =
    button;
  return (
    <Button
      data-testid={DATA_TEST_IDS.BUTTON('action-card')}
      className={'flex justify-center items-center'}
      to={buttonHref}
      variant={variant ?? buttonType}
      openInNewTab={openInNewTab}
      width={buttonWidth ?? 'auto'}
      height={'50px'}
    >
      {buttonText}
    </Button>
  );
};
export function ThreeCardSection({
  title,
  button,
  description,
  items,
  showCategories,
}: Omit<ThreeCardEntity, '_type'>) {
  const {buttonText} = button ?? {};
  return (
    <PageSectionContainer className={'pr-0'}>
      <div className="flex justify-between items-end max-w-full pr-5 xl:pr-0">
        <div className={'max-w-full w-[720px]'}>
          {title?.text && (
            <div
              className={clsx(
                'flex justify-start align-center mb-2.5 lg:mb-[7px]',
                description?.text ? 'lg:mb-[7px]' : 'lg:mb-[25px]',
              )}
            >
              <TextInternal
                className={clsx(
                  title.variant === 'span' &&
                    'text-xl md:text-3xl md:leading-[44px] font-extrabold font-header',
                )}
                {...title}
              />
            </div>
          )}
          {description?.text && (
            <div className={clsx(title && 'mb-5 lg:mb-[1.875rem]')}>
              <TextInternal
                // Match style of default option with design
                // span means default
                className={clsx(
                  description.variant === 'span' &&
                    'text-sm leading-[19px] md:text-base md:leading-5 font-normal',
                )}
                {...description}
                // We will use p because line height doesn't match with design with span
                variant={
                  description.variant === 'span' ? 'p' : description.variant
                }
              />
            </div>
          )}
        </div>
        {button && buttonText && (
          <div className={'mb-[25px] hidden md:block'}>
            <ActionButton button={button} />
          </div>
        )}
      </div>
      <div className={'md:mr-5 xl:mr-0'}>
        <Swiper
          // @ts-ignore
          slidesPerView={1.62}
          spaceBetween={10}
          effect="fade"
          shortSwipes={true}
          threshold={10}
          allowTouchMove={true}
          breakpoints={{
            // width for 3 mobile cards fitting in a screen
            819: {
              slidesPerView: 3,
              spaceBetween: 10,
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 38,
            },
          }}
        >
          {items.map?.(
            ({
              key,
              image,
              title,
              description,
              link,
              openInNewTab,
              categories,
            }) => (
              <SwiperSlide key={key ?? title}>
                <div>
                  <Card
                    image={image}
                    title={title}
                    description={description}
                    link={link}
                    openInNewTab={openInNewTab}
                    categories={showCategories ? categories : undefined}
                  />
                </div>
              </SwiperSlide>
            ),
          )}
        </Swiper>
        {button && buttonText && (
          <div
            className={
              'w-full md:hidden flex justify-center items-center mt-[2.125rem] pr-5'
            }
          >
            <ActionButton button={button} variant={'gray-light'} />
          </div>
        )}
      </div>
    </PageSectionContainer>
  );
}
